import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { track } from "@/lib/analytics";
import { getAttribution } from "@/lib/utm";
import { LEAD_MAGNETS } from "@/data/leadMagnets";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Reusable lead-magnet download dialog.
 * Captures name / work email / company (+ attribution + consent), then
 * delivers the PDF instantly on backend-confirmed success.
 */
export default function LeadMagnetDialog({ open, onOpenChange, resource }) {
  const magnet = LEAD_MAGNETS[resource];
  const [form, setForm] = useState({ name: "", email: "", company: "", website: "" });
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (!magnet) return null;

  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const reset = () => {
    setForm({ name: "", email: "", company: "", website: "" });
    setConsent(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.company.trim()) {
      toast.error("Please fill in your name, work email and company.");
      return;
    }
    setSubmitting(true);
    const attr = getAttribution();
    try {
      await axios.post(`${API}/lead-magnet`, {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim(),
        resource,
        marketing_consent: consent,
        source_page: window.location.pathname,
        referrer: attr.referrer || null,
        utm_source: attr.utm_source || null,
        utm_medium: attr.utm_medium || null,
        utm_campaign: attr.utm_campaign || null,
        utm_content: attr.utm_content || null,
        utm_term: attr.utm_term || null,
        website: form.website || null, // honeypot
      });
      track("lead_magnet_download", { resource, source_page: window.location.pathname });
      // Instant delivery — browser download of the hosted PDF.
      const a = document.createElement("a");
      a.href = magnet.file;
      a.download = magnet.file.split("/").pop();
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("Your diagnostic is downloading.", {
        description: "We've also emailed you a copy.",
      });
      reset();
      onOpenChange(false);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Something went wrong — your input is preserved.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent
        data-testid="leadmagnet-dialog"
        className="max-w-lg rounded-none border-zinc-200 p-0 gap-0 bg-white"
      >
        <div className="px-8 pt-8 pb-4 border-b border-zinc-200">
          <DialogHeader className="text-left space-y-2">
            <div className="text-[10px] uppercase tracking-[0.28em] text-black font-semibold">
              Free Diagnostic
            </div>
            <DialogTitle className="font-serif-display text-2xl sm:text-3xl leading-tight font-normal">
              {magnet.title}
            </DialogTitle>
            <DialogDescription className="text-zinc-600 text-sm">
              Twenty scored checks with interpretation and next steps. Tell us
              where to send it — the download starts immediately.
            </DialogDescription>
          </DialogHeader>
        </div>

        <form onSubmit={submit} className="px-8 py-6 space-y-4">
          <Field label="Full name*" htmlFor="lm-name">
            <Input id="lm-name" data-testid="lm-name" value={form.name} onChange={setField("name")} required
              className="rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-black h-11" />
          </Field>
          <Field label="Work email*" htmlFor="lm-email">
            <Input id="lm-email" data-testid="lm-email" type="email" value={form.email} onChange={setField("email")} required
              className="rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-black h-11" />
          </Field>
          <Field label="Company name*" htmlFor="lm-company">
            <Input id="lm-company" data-testid="lm-company" value={form.company} onChange={setField("company")} required
              className="rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-black h-11" />
          </Field>

          {/* Honeypot */}
          <div aria-hidden="true" className="absolute -left-[9999px] -top-[9999px] h-0 w-0 overflow-hidden">
            <label htmlFor="lm-website">Website</label>
            <input id="lm-website" data-testid="lm-website" type="text" tabIndex={-1} autoComplete="off"
              value={form.website} onChange={setField("website")} />
          </div>

          <label className="flex items-start gap-2.5 text-xs text-zinc-500 cursor-pointer">
            <Checkbox
              data-testid="lm-consent"
              checked={consent}
              onCheckedChange={(v) => setConsent(!!v)}
              className="mt-0.5 rounded-none"
            />
            <span>
              I'd like occasional BLEZZINGS perspectives by email. Optional —
              the diagnostic is yours either way.
            </span>
          </label>

          <DialogFooter className="px-0 pt-1 sm:justify-between gap-3">
            <p className="text-xs text-zinc-500">
              Your details stay private —{" "}
              <a href="/privacy-policy" className="underline underline-offset-2 hover:text-black">
                Privacy Policy
              </a>
              .
            </p>
            <button
              data-testid="lm-submit"
              type="submit"
              disabled={submitting}
              className="group inline-flex items-center justify-center gap-2 h-11 px-6 bg-black text-white text-sm font-medium hover:bg-zinc-800 transition-colors duration-300 disabled:opacity-60"
            >
              {submitting ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Preparing</>
              ) : (
                <>{magnet.cta}<ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></>
              )}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, htmlFor, children }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor} className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">
        {label}
      </Label>
      {children}
    </div>
  );
}
